import * as React from "react";
import Drawer, { DrawerProps } from "@mui/material/Drawer";
import Box, { BoxProps } from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { SxProps, Theme, alpha, useTheme } from "@mui/material/styles";

export interface GlassDrawerProps extends Omit<DrawerProps, "children"> {
  children: React.ReactNode;
  title?: string;
  contentProps?: Omit<BoxProps, "children">;
  cardProps?: Omit<BoxProps, "children">;
}

const GlassDrawer: React.FC<GlassDrawerProps> = ({
  children,
  title,
  contentProps,
  cardProps,
  PaperProps,
  ...drawerProps
}) => {
  const theme = useTheme();

  const {
    sx: cardSx,
    ...restCardProps
  } = cardProps || {};
  const {
    sx: contentSx,
    ...restContentProps
  } = contentProps || {};

  const defaultPaperSx: SxProps<Theme> = {
    width: { xs: "100%", sm: 420, md: 460 },
    maxWidth: "100%",
    top: 0,
    height: "100vh",
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    background: "transparent",
    boxShadow: "none",
    border: "none",
  };

  const userPaperSx = PaperProps?.sx;
  const mergedPaperSx: SxProps<Theme> = Array.isArray(userPaperSx)
    ? [defaultPaperSx, ...userPaperSx]
    : userPaperSx
      ? [defaultPaperSx, userPaperSx]
      : defaultPaperSx;

  const mergedPaperProps: DrawerProps["PaperProps"] = {
    ...PaperProps,
    sx: mergedPaperSx,
  };

  const isDark = theme.palette.mode === "dark";
  const glassConfig = {
    blur: "blur(35px) saturate(220%)",
    bgLight: alpha("#ffffff", 0.6),
    bgDark: alpha("#ffffff", 0.4),
    borderLight: alpha("#ffffff", 0.7),
    borderDark: alpha("#ffffff", 0.6),
    shadowLight: "0 8px 32px 0 rgba(31, 38, 135, 0.25)",
    shadowDark: "0 8px 32px 0 rgba(0, 0, 0, 0.6)",
    insetLight: "inset 0 1px 0 0 rgba(255, 255, 255, 0.6)",
    insetDark: "inset 0 1px 0 0 rgba(255, 255, 255, 0.4)",
  };

  return (
    <Drawer PaperProps={mergedPaperProps} {...drawerProps}>
      <Box
        sx={{
          width: "100%",
          height: "100vh",
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          borderRadius: (theme) => theme.shape.borderRadius * 2,
          background: isDark ? glassConfig.bgDark : glassConfig.bgLight,
          border: `1px solid ${isDark ? glassConfig.borderDark : glassConfig.borderLight}`,
          boxShadow: isDark ? glassConfig.shadowDark : glassConfig.shadowLight,
          backdropFilter: glassConfig.blur,
          WebkitBackdropFilter: glassConfig.blur,
          position: "relative",
          overflow: "hidden",
          boxSizing: "border-box",
          "::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: (theme) => theme.shape.borderRadius * 2,
            pointerEvents: "none",
            boxShadow: isDark ? glassConfig.insetDark : glassConfig.insetLight,
            zIndex: 1,
          },
          ...cardSx,
        }}
        {...restCardProps}
      >
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "45%",
            background: `linear-gradient(180deg, ${alpha("#ffffff", isDark ? 0.18 : 0.28)} 0%, transparent 100%)`,
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            borderRadius: (theme) => theme.shape.borderRadius * 2,
            border: `1px solid ${alpha("#ffffff", 0.2)}`,
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: `linear-gradient(135deg, ${alpha("#ffffff", isDark ? 0.08 : 0.12)} 0%, transparent 45%, ${alpha("#ffffff", isDark ? 0.05 : 0.08)} 100%)`,
            pointerEvents: "none",
            zIndex: 1,
          }}
        />
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: alpha("#ffffff", isDark ? 0.05 : 0.08),
            pointerEvents: "none",
            zIndex: 1,
            borderRadius: (theme) => theme.shape.borderRadius * 2,
          }}
        />
        {title && (
          <Box
            sx={{
              px: { xs: 1.5, sm: 2 },
              pt: { xs: 2, sm: 3 },
              pb: { xs: 1, sm: 1.5 },
              borderBottom: `1px solid ${theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(0, 0, 0, 0.06)"}`,
              backgroundColor: theme.palette.mode === "dark" ? "rgba(255, 255, 255, 0.02)" : "rgba(0, 0, 0, 0.01)",
              zIndex: 2,
              position: "relative",
            }}
          >
            <Typography
              variant="h6"
              component="h2"
              fontWeight={600}
              sx={{
                color: "#F4A259",
                fontSize: {
                  xs: "1.05rem",
                  sm: "1.15rem",
                  md: "1.25rem",
                },
                lineHeight: { xs: 1.2, sm: 1.3 },
              }}
            >
              {title}
            </Typography>
          </Box>
        )}
        <Box
          sx={{
            width: "100%",
            height: "100%",
            flex: 1,
            display: "flex",
            flexDirection: "column",
            overflowY: "auto",
            minHeight: 0,
            px: { xs: 1.5, sm: 2 },
            py: { xs: 1.5, sm: 2 },
            pt: title ? { xs: 1.5, sm: 2 } : { xs: 1.5, sm: 4 },
            boxSizing: "border-box",
            gap: 2,
            zIndex: 2,
            position: "relative",
            ...contentSx,
          }}
          {...restContentProps}
        >
          {children}
        </Box>
      </Box>
    </Drawer>
  );
};

export default GlassDrawer;


